import { type CheckpointField, checkpointFieldsOf, rowIsLate } from '@ofmos/shared/checklist';
import { useMemo, useState } from 'react';
import { useRecordsByIds } from '../../../data/hooks';
import { SegmentedControl } from '../../../ui';
import { toCheckpointField } from '../../record/checklist';
import { todayKey } from '../../record/dueDate';
import type { TableColumn } from './tableModel';

/**
 * THE "LATE" CHIP (`docs/decisions/phase-b-coach.md` D6).
 *
 * WHY IT IS NOT A SAVED FILTER, and this is the whole reason it exists as its own mechanism:
 * "late" is `base + offsetDays < today` computed PER ROW over two of that row's own columns,
 * and the stored filter AST has no field-plus-offset comparison and never will (`shared/
 * view.ts`). Materialising it as a date would be a filter that is wrong tomorrow morning.
 *
 * SO IT IS A BROWSER PREDICATE OVER THE ROWS ALREADY LOADED, and it says so out loud. A walk
 * that has fetched 50 of 300 rows can only answer for 50, and a chip that quietly implied
 * otherwise would be the "plausible-looking empty answer" this repository refuses everywhere
 * else. The line under the toolbar names the number it is speaking for.
 *
 * IT COSTS NO REQUEST. `useRecordsByIds` reads the canonical entries the list query already
 * seeded (INV-13) - the same entries every row component reads - so the predicate runs over
 * data that is on the machine.
 *
 * "MINE" IS NOT BUILT, and the document already defers it (D6): it needs a current-user
 * operand for RELATION fields plus the team_member-to-user join by e-mail. Recorded in the
 * slice's record rather than in the ledger, because the decision was made before the build.
 */

export type CheckpointSegment = 'all' | 'late';

export interface CheckpointFilter {
  /** Null when this view has no checkpoint columns, which is when nothing renders. */
  readonly fields: readonly CheckpointField[] | null;
  readonly segment: CheckpointSegment;
  readonly setSegment: (next: CheckpointSegment) => void;
  /** How many of the LOADED rows are past a deadline. */
  readonly lateCount: number;
  /** The ids to render: every loaded row, or only the late ones. */
  readonly visibleIds: readonly string[] | undefined;
}

export function useCheckpointFilter(input: {
  readonly objectId: string;
  readonly columns: readonly TableColumn[];
  readonly rowIds: readonly string[] | undefined;
  /**
   * Off in grouped mode. A group is a property of a SET and the grouped path never holds one
   * flat list of ids, so a predicate over "the loaded rows" has nothing to be about there.
   */
  readonly enabled: boolean;
}): CheckpointFilter {
  const [segment, setSegment] = useState<CheckpointSegment>('all');

  const fields = useMemo(() => {
    const carried = checkpointFieldsOf(
      input.columns.map((column) => toCheckpointField(column.field)),
    );
    return carried.length === 0 ? null : carried;
  }, [input.columns]);

  const active = input.enabled && fields !== null;
  const rows = useRecordsByIds(input.objectId, active ? (input.rowIds ?? []) : []);

  const lateIds = useMemo(() => {
    if (!active || fields === null) return null;
    const today = todayKey();
    return new Set(
      rows.records.filter((row) => rowIsLate(row.values, fields, today)).map((row) => row.id),
    );
  }, [active, fields, rows.records]);

  const visibleIds =
    segment === 'late' && lateIds !== null
      ? input.rowIds?.filter((id) => lateIds.has(id))
      : input.rowIds;

  return {
    fields: active ? fields : null,
    segment,
    setSegment,
    lateCount: lateIds?.size ?? 0,
    visibleIds,
  };
}

/**
 * The control, at BOTH widths and in its own row rather than inside the desktop toolbar.
 *
 * The toolbar is desktop-only since 2026-07-29 - below `md:` its four controls are sections
 * of the Adjust sheet - and "late" is the one narrowing a phone needs MOST: the card list is
 * where a coach scans a cohort on the way to a call. A sheet section would put it two taps
 * away behind a control that says "Adjust".
 */
export function CheckpointFilterBar({
  filter,
  loadedRows,
  hasMore,
}: {
  readonly filter: CheckpointFilter;
  readonly loadedRows: number;
  readonly hasMore: boolean;
}) {
  if (filter.fields === null) return null;

  return (
    <div className="flex flex-col gap-1 pb-3">
      <SegmentedControl
        size="sm"
        className="self-start"
        value={filter.segment}
        onValueChange={(next) => filter.setSegment(next === 'late' ? 'late' : 'all')}
        ariaLabel="Deadlines"
        segments={[
          { value: 'all', label: 'All' },
          { value: 'late', label: `Late (${filter.lateCount})` },
        ]}
      />
      {hasMore && (
        <p className="text-2xs text-ink-subtle">{`Late among the ${loadedRows} rows loaded.`}</p>
      )}
    </div>
  );
}
